import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Form, Input, Select, Switch, InputNumber, Button, Space, Row, Col, App,
  Table, Tag, Modal, Empty, Divider, Upload, Radio, message as antdMessage
} from 'antd';
import {
  PrinterOutlined, SettingOutlined, SearchOutlined, FileTextOutlined,
  UploadOutlined, ReloadOutlined, DownloadOutlined, CloseCircleFilled
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import request from '../utils/request';
import { getCurrentUser } from '../utils/user';

const STORAGE_KEY = 'print_template_settings';

const DEFAULT_SETTINGS = {
  paperSize: 'A4',
  orientation: 'portrait',
  layout: 4,
  thermalHeight: 100,
  customThermalWidth: 80,
  customThermalHeight: 100,
  headerText: '物业访客通行证',
  subheaderText: '请访客在到访时出示此证',
  logo: '',
  showLogo: false,
  showName: true,
  showPhone: true,
  showRoom: true,
  showDate: true,
  showTime: true,
  showPurpose: false,
  showId: true,
  showPrintTime: true,
  qrCodeSize: 'medium',
  borderStyle: 'solid',
  showWatermark: false,
  watermarkText: '物业访客通行证',
  titleFontSize: null,
  subtitleFontSize: null,
  infoFontSize: null,
  labelFontSize: null,
  idFontSize: null,
  footerFontSize: null
};

const PAPER_SIZES = {
  A4: { width: 210, height: 297, type: 'standard', label: 'A4 (210×297mm)' },
  A5: { width: 148, height: 210, type: 'standard', label: 'A5 (148×210mm)' },
  A6: { width: 105, height: 148, type: 'standard', label: 'A6 (105×148mm)' },
  thermal_58: { width: 58, height: 100, type: 'thermal', label: '58mm 热敏小票' },
  thermal_80: { width: 80, height: 100, type: 'thermal', label: '80mm 热敏小票' },
  thermal_100: { width: 100, height: 150, type: 'thermal', label: '100mm 热敏小票' },
  thermal_custom: { width: 80, height: 100, type: 'thermal_custom', label: '自定义热敏...' },
  label_40x30: { width: 40, height: 30, type: 'label', label: '40×30mm 标签' },
  label_50x30: { width: 50, height: 30, type: 'label', label: '50×30mm 标签' },
  label_50x50: { width: 50, height: 50, type: 'label', label: '50×50mm 标签' },
  label_60x40: { width: 60, height: 40, type: 'label', label: '60×40mm 标签' },
  label_70x37: { width: 70, height: 37, type: 'label', label: '70×37mm 标签' },
  label_80x50: { width: 80, height: 50, type: 'label', label: '80×50mm 标签' },
  label_80x80: { width: 80, height: 80, type: 'label', label: '80×80mm 标签' },
  label_100x100: { width: 100, height: 100, type: 'label', label: '100×100mm 标签' }
};

const getPaperType = (size) => PAPER_SIZES[size]?.type || 'standard';

const LAYOUT_OPTIONS = [
  { value: 1, label: '1张/页（大图）' },
  { value: 2, label: '2张/页' },
  { value: 4, label: '4张/页（2×2）' },
  { value: 6, label: '6张/页（2×3）' },
  { value: 8, label: '8张/页（2×4）' },
  { value: 9, label: '9张/页（3×3）' }
];

const QR_SIZES = {
  small: 60,
  medium: 90,
  large: 120
};

const PrintTemplate = () => {
  const { message } = App.useApp();
  const [searchParams] = useSearchParams();
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);
  const fixedCommunityId = !isSuperAdmin ? currentUser.communityId : null;
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('approved');
  const [communityFilter, setCommunityFilter] = useState(fixedCommunityId);
  const [communities, setCommunities] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const printRef = useRef(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try { setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(saved) }); }
      catch (e) { console.error('读取设置失败', e); }
    }
    fetchCommunities();
    fetchAppointments();
  }, []);

  const fetchCommunities = async () => {
    try {
      const res = await request.get('/community', { params: { page: 1, pageSize: 999 } });
      if (res.code === 200) setCommunities(res.data.list || []);
    } catch (e) { console.error('Failed to fetch communities:', e); }
  };

  useEffect(() => {
    const idsParam = searchParams.get('ids');
    if (idsParam) {
      const ids = idsParam.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
      if (ids.length > 0) setSelectedRowKeys(ids);
    }
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    if (!previewMode) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      const node = printRef.current;
      if (!node) {
        if (!cancelled) setPreviewMode(false);
        return;
      }
      if (cancelled) return;
      const clone = node.cloneNode(true);
      const dims = getCurrentPaperDims();
      const styles = printStyleContent;
      const paperLabel = settings.paperSize;
      const dimText = `${dims.width} × ${dims.height} mm`;
      const win = window.open('', '_blank', 'width=900,height=1200');
      if (!win) {
        antdMessage.error('弹窗被拦截,请在浏览器允许 pop-up 后重试');
        setPreviewMode(false);
        return;
      }
      win.document.open();
      win.document.write(`<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<title>访客通行证</title>
<style>
${styles}
body { margin: 0; padding: 0; background: #fff; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif; }
.no-print { display: block; }
@media print {
  .no-print { display: none !important; }
}
.warn-bar {
  position: sticky; top: 0; z-index: 9999;
  background: linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%);
  color: #fff; padding: 14px 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  animation: pulse 1.5s ease-in-out 3;
}
@keyframes pulse {
  0%, 100% { box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
  50% { box-shadow: 0 4px 20px rgba(255, 77, 79, 0.7); }
}
.warn-title { font-size: 16px; font-weight: 700; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; }
.warn-steps { font-size: 13px; line-height: 1.8; margin-bottom: 10px; opacity: 0.96; }
.warn-steps b { background: rgba(255,255,255,0.25); padding: 1px 6px; border-radius: 3px; }
.warn-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.warn-row label { display: flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }
.warn-row input[type=checkbox] { width: 16px; height: 16px; cursor: pointer; }
.print-btn {
  background: #fff; color: #cf1322; border: none;
  padding: 8px 22px; border-radius: 4px; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
}
.print-btn:disabled { background: rgba(255,255,255,0.4); color: rgba(255,255,255,0.7); cursor: not-allowed; }
.print-btn:not(:disabled):hover { background: #fff5f5; transform: translateY(-1px); }
</style>
</head>
<body>
<div class="warn-bar no-print">
  <div class="warn-title">⚠️ 打印前必读:模板纸张 <b style="background:rgba(255,255,255,0.25);padding:1px 8px;border-radius:3px;">${paperLabel}</b> (${dimText})</div>
  <div class="warn-steps">
    Chrome 对话框<b>默认选中 A4</b>,请按下述步骤操作:
    1. 勾选下方"已确认纸张"<br>
    2. 点击"打开打印对话框"<br>
    3. 在对话框<b>纸张/大小</b>下拉里手动选 <b>${paperLabel}</b> (${dimText})<br>
    4. 再点"打印"出纸
  </div>
  <div class="warn-row">
    <label><input type="checkbox" id="ack" onchange="document.getElementById('printBtn').disabled = !this.checked"> 我已确认选择 <b>${paperLabel}</b> 纸张</label>
    <button id="printBtn" class="print-btn" disabled onclick="window.print()">打开打印对话框</button>
  </div>
</div>
${clone.outerHTML}
</body>
</html>`);
      win.document.close();
      setPreviewMode(false);
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [previewMode]);

  const fetchAppointments = async (status = statusFilter, keyword = '', communityId = communityFilter, page = 1, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = { page: 1, pageSize: 1000 };
      if (status && status !== 'all') params.status = status;
      if (!isSuperAdmin) {
        params.communityId = fixedCommunityId;
      } else if (communityId) {
        params.communityId = communityId;
      }
      const res = await request.get('/appointment/list', { params });
      if (res.code === 200) {
        let list = res.data.list || [];
        if (keyword) {
          const lower = keyword.toLowerCase();
          list = list.filter(a =>
            (a.visitor_name || '').toLowerCase().includes(lower) ||
            (a.visitor_phone || '').includes(keyword) ||
            String(a.id).includes(keyword) ||
            (a.room_info || '').toLowerCase().includes(lower)
          );
        }
        const total = list.length;
        const start = (page - 1) * pageSize;
        const paged = list.slice(start, start + pageSize);
        setAppointments(paged);
        setPagination({ current: page, pageSize, total });
      }
    } catch (e) {
      antdMessage.error('获取预约失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pag) => {
    setPagination(p => ({ ...p, current: pag.current, pageSize: pag.pageSize }));
    fetchAppointments(statusFilter, searchText, communityFilter, pag.current, pag.pageSize);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    Modal.confirm({
      title: '重置设置',
      content: '确定要恢复默认设置吗?',
      onOk: () => setSettings(DEFAULT_SETTINGS)
    });
  };

  const handlePrint = () => {
    if (selectedRowKeys.length === 0) {
      antdMessage.warning('请至少选择一条预约');
      return;
    }
    setPreviewMode(true);
  };

  const handleLogoUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setSettings(prev => ({ ...prev, logo: e.target.result, showLogo: true }));
    };
    reader.readAsDataURL(file);
    return false;
  };

  const getGridStyle = () => {
    const pType = getPaperType(settings.paperSize);
    if (pType === 'thermal' || pType === 'thermal_custom' || pType === 'label') {
      return { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
    }
    const cols = settings.layout === 1 ? 1 : settings.layout === 2 ? 2 :
                 settings.layout <= 4 ? 2 : settings.layout <= 6 ? 2 : 3;
    const rows = Math.ceil(settings.layout / cols);
    return { gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` };
  };

  const getCurrentPaperDims = () => {
    const p = PAPER_SIZES[settings.paperSize];
    if (!p) return { width: 210, height: 297 };
    let width, height;
    if (p.type === 'thermal') {
      width = p.width;
      height = settings.thermalHeight || p.height;
    } else if (p.type === 'thermal_custom') {
      width = settings.customThermalWidth;
      height = settings.customThermalHeight;
    } else {
      width = p.width;
      height = p.height;
    }
    if (settings.orientation === 'landscape' && width !== height) {
      return { width: height, height: width };
    }
    return { width, height };
  };

  const isCompactPaper = () => {
    const pType = getPaperType(settings.paperSize);
    return pType === 'thermal' || pType === 'thermal_custom' || pType === 'label';
  };

  const renderCard = (appt, idx) => {
    if (!appt) return null;
    const compact = isCompactPaper();
    let qrSize = QR_SIZES[settings.qrCodeSize] || QR_SIZES.medium;
    if (compact) {
      const dims = getCurrentPaperDims();
      if (settings.qrCodeSize === 'large') qrSize = Math.min(120, Math.floor(dims.width * 3.2));
      else if (settings.qrCodeSize === 'small') qrSize = Math.min(60, Math.floor(dims.width * 1.6));
      else qrSize = Math.min(90, Math.floor(dims.width * 2.4));
    }
    return (
      <div key={appt.id || idx} className="print-card" style={{ borderStyle: settings.borderStyle }}>
        <div className="print-card-header">
          {settings.showLogo && settings.logo && (
            <img src={settings.logo} alt="logo" className="print-logo" />
          )}
          <div className="print-card-titles">
            <div className="print-card-title">{settings.headerText}</div>
            {settings.subheaderText && (
              <div className="print-card-subtitle">{settings.subheaderText}</div>
            )}
          </div>
        </div>

        <div className="print-card-body">
          <div className="print-card-info">
            {settings.showName && (
              <div className="print-info-row">
                <span className="print-info-label">访客:</span>
                <span className="print-info-value">{appt.visitor_name || '-'}</span>
              </div>
            )}
            {settings.showPhone && (
              <div className="print-info-row">
                <span className="print-info-label">电话:</span>
                <span className="print-info-value">{appt.visitor_phone || '-'}</span>
              </div>
            )}
            {settings.showRoom && (
              <div className="print-info-row">
                <span className="print-info-label">房号:</span>
                <span className="print-info-value">{appt.room_info || '-'}</span>
              </div>
            )}
            {settings.showDate && (
              <div className="print-info-row">
                <span className="print-info-label">日期:</span>
                <span className="print-info-value">{appt.visit_date || '-'}</span>
              </div>
            )}
            {settings.showTime && (
              <div className="print-info-row">
                <span className="print-info-label">时间:</span>
                <span className="print-info-value">{appt.visit_time || '-'}</span>
              </div>
            )}
            {settings.showPurpose && appt.purpose && (
              <div className="print-info-row">
                <span className="print-info-label">目的:</span>
                <span className="print-info-value">{appt.purpose}</span>
              </div>
            )}
          </div>

          <div className="print-card-qr">
            {appt.qr_code ? (
              <img src={appt.qr_code} alt="QR" style={{ width: qrSize, height: qrSize }} />
            ) : (
              <div style={{ width: qrSize, height: qrSize, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 12 }}>
                无二维码
              </div>
            )}
            {settings.showId && (
              <div className="print-card-id">#{appt.id}</div>
            )}
          </div>
        </div>

        {settings.showPrintTime && (
          <div className="print-card-footer">
            打印时间: {new Date().toLocaleString('zh-CN')}
          </div>
        )}
      </div>
    );
  };

  const selectedAppointments = appointments.filter(a => selectedRowKeys.includes(a.id));
  const previewAppointments = selectedAppointments.length > 0
    ? selectedAppointments
    : appointments.filter(a => a.status === 'approved').slice(0, settings.layout);

  const printStyleContent = (() => {
    const dims = getCurrentPaperDims();
    const compact = isCompactPaper();
    const pageSize = `${dims.width}mm ${dims.height}mm`;
    const margin = compact ? '2mm' : '8mm';
    const titleSizeAuto = compact ? '11pt' : (settings.layout <= 2 ? '16pt' : settings.layout <= 4 ? '12pt' : '10pt');
    const subtitleSizeAuto = compact ? '7pt' : (settings.layout <= 2 ? '9pt' : '7pt');
    const infoSizeAuto = compact ? '8pt' : (settings.layout <= 2 ? '10pt' : settings.layout <= 4 ? '8pt' : '7pt');
    const labelSizeAuto = compact ? '7pt' : (settings.layout <= 2 ? '9pt' : settings.layout <= 4 ? '8pt' : '6pt');
    const idSizeAuto = compact ? '7pt' : (settings.layout <= 2 ? '9pt' : '7pt');
    const footerSizeAuto = compact ? '6pt' : (settings.layout <= 2 ? '8pt' : '6pt');
    const titleSize = settings.titleFontSize ? `${settings.titleFontSize}pt` : titleSizeAuto;
    const subtitleSize = settings.subtitleFontSize ? `${settings.subtitleFontSize}pt` : subtitleSizeAuto;
    const infoSize = settings.infoFontSize ? `${settings.infoFontSize}pt` : infoSizeAuto;
    const labelSize = settings.labelFontSize ? `${settings.labelFontSize}pt` : labelSizeAuto;
    const idSize = settings.idFontSize ? `${settings.idFontSize}pt` : idSizeAuto;
    const footerSize = settings.footerFontSize ? `${settings.footerFontSize}pt` : footerSizeAuto;
    const cardPadding = compact ? '2mm' : '4mm';

    return `
    @page { size: ${pageSize}; margin: ${margin}; }
    .print-area { width: 100%; }
    .print-grid { display: grid; gap: ${compact ? '0' : '5mm'}; ${Object.entries(getGridStyle()).map(([k, v]) => `${k}: ${v};`).join(' ')} }
    .print-card { border: 1.5px ${settings.borderStyle} #333; border-radius: ${compact ? '2px' : '6px'}; padding: ${cardPadding}; background: #fff; page-break-inside: avoid; display: flex; flex-direction: column; }
    .print-card-header { display: flex; align-items: center; gap: 2mm; padding-bottom: 1.5mm; border-bottom: 1px dashed #ccc; margin-bottom: 2mm; }
    .print-logo { width: ${compact ? '8mm' : '12mm'}; height: ${compact ? '8mm' : '12mm'}; object-fit: contain; }
    .print-card-titles { flex: 1; }
    .print-card-title { font-size: ${titleSize}; font-weight: bold; color: #1890ff; }
    .print-card-subtitle { font-size: ${subtitleSize}; color: #999; margin-top: 0.5mm; }
    .print-card-body { display: flex; gap: 2mm; flex: 1; }
    .print-card-info { flex: 1; display: flex; flex-direction: column; gap: ${compact ? '0.8mm' : '1.5mm'}; }
    .print-info-row { display: flex; gap: 1mm; font-size: ${infoSize}; }
    .print-info-label { color: #999; min-width: ${compact ? '10mm' : '14mm'}; font-size: ${labelSize}; }
    .print-info-value { color: #333; font-weight: 500; word-break: break-all; }
    .print-card-qr { display: flex; flex-direction: column; align-items: center; gap: 0.5mm; }
    .print-card-qr img { display: block; }
    .print-card-id { font-size: ${idSize}; color: #666; }
    .print-card-footer { font-size: ${footerSize}; color: #999; text-align: right; margin-top: 1mm; padding-top: 1mm; border-top: 1px dashed #eee; }
  `;
  })();

  return (
    <div className="print-template-page">
      <style dangerouslySetInnerHTML={{ __html: `
        ${printStyleContent}
        .print-template-page .ant-card { margin-bottom: 16px; }
        .print-template-page .preview-area { background: #e8e8e8; padding: 20px; min-height: 400px; max-height: 600px; overflow: auto; }
        .print-preview-paper { background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.15); margin: 0 auto; padding: 8mm; }
        .print-empty { padding: 60px 20px; text-align: center; color: #999; }
        .toolbar-select.ant-select { height: 36px; }
        .toolbar-select.ant-select .ant-select-selector { height: 36px !important; padding: 0 11px !important; }
        .toolbar-select.ant-select .ant-select-selection-item { line-height: 34px !important; }
        @media print {
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .ant-modal-mask, .ant-modal-wrap { display: none !important; }
        }
      ` }} />

      <Row gutter={16} style={previewMode ? { display: 'none' } : {}}>
        <Col span={8}>
          <Card title={<><SettingOutlined /> 模板设置</>} size="small" extra={<Button size="small" icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>}>
            <Form layout="vertical" size="small">
              <Form.Item label="纸张类型">
                <Select
                  value={getPaperType(settings.paperSize)}
                  onChange={t => {
                    const first = Object.keys(PAPER_SIZES).find(k => PAPER_SIZES[k].type === t);
                    if (first) {
                      const newSettings = { paperSize: first };
                      if (t !== 'standard') newSettings.layout = 1;
                      setSettings(prev => ({ ...prev, ...newSettings }));
                    }
                  }}
                  options={[
                    { value: 'standard', label: '📄 标准纸张' },
                    { value: 'thermal', label: '🧾 热敏小票' },
                    { value: 'thermal_custom', label: '⚙️ 自定义热敏' },
                    { value: 'label', label: '🏷️ 标签贴纸' }
                  ]}
                />
              </Form.Item>

              <Form.Item label="纸张大小">
                <Select
                  value={settings.paperSize}
                  onChange={v => {
                    const pType = getPaperType(v);
                    const newSettings = { paperSize: v };
                    if (pType !== 'standard') newSettings.layout = 1;
                    setSettings(prev => ({ ...prev, ...newSettings }));
                  }}
                >
                  <Select.OptGroup label="标准纸张">
                    {Object.keys(PAPER_SIZES).filter(k => PAPER_SIZES[k].type === 'standard').map(k => (
                      <Select.Option key={k} value={k}>{PAPER_SIZES[k].label}</Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="热敏小票">
                    {Object.keys(PAPER_SIZES).filter(k => PAPER_SIZES[k].type === 'thermal').map(k => (
                      <Select.Option key={k} value={k}>{PAPER_SIZES[k].label}</Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="标签贴纸">
                    {Object.keys(PAPER_SIZES).filter(k => PAPER_SIZES[k].type === 'label').map(k => (
                      <Select.Option key={k} value={k}>{PAPER_SIZES[k].label}</Select.Option>
                    ))}
                  </Select.OptGroup>
                </Select>
              </Form.Item>

              <Form.Item label="打印方向">
                <Radio.Group
                  value={settings.orientation}
                  onChange={e => updateSetting('orientation', e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="portrait">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 14, border: '1.5px solid currentColor', borderRadius: 1 }} />
                      纵向
                    </span>
                  </Radio.Button>
                  <Radio.Button value="landscape">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ display: 'inline-block', width: 14, height: 10, border: '1.5px solid currentColor', borderRadius: 1 }} />
                      横向
                    </span>
                  </Radio.Button>
                </Radio.Group>
                <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>
                  当前纸张: {getCurrentPaperDims().width} × {getCurrentPaperDims().height} mm
                  {settings.orientation === 'landscape' && ' (横向已交换)'}
                </div>
              </Form.Item>

              {getPaperType(settings.paperSize) === 'thermal' && (
                <Form.Item label="小票高度 (mm)">
                  <InputNumber
                    value={settings.thermalHeight}
                    onChange={v => updateSetting('thermalHeight', v || 100)}
                    min={50}
                    max={500}
                    step={10}
                    style={{ width: '100%' }}
                    addonAfter="mm"
                  />
                </Form.Item>
              )}

              {getPaperType(settings.paperSize) === 'thermal_custom' && (
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item label="宽度 (mm)">
                      <InputNumber
                        value={settings.customThermalWidth}
                        onChange={v => updateSetting('customThermalWidth', v || 80)}
                        min={20}
                        max={200}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="高度 (mm)">
                      <InputNumber
                        value={settings.customThermalHeight}
                        onChange={v => updateSetting('customThermalHeight', v || 100)}
                        min={20}
                        max={500}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {getPaperType(settings.paperSize) === 'standard' && (
                <Form.Item label="每页张数">
                  <Select
                    value={settings.layout}
                    onChange={v => updateSetting('layout', v)}
                    options={LAYOUT_OPTIONS}
                  />
                </Form.Item>
              )}

              {getPaperType(settings.paperSize) !== 'standard' && (
                <Form.Item>
                  <Tag color="orange">热敏/标签模式：每页1张，自动适配纸张宽度</Tag>
                </Form.Item>
              )}

              <Form.Item label="二维码大小">
                <Select
                  value={settings.qrCodeSize}
                  onChange={v => updateSetting('qrCodeSize', v)}
                  options={[
                    { value: 'small', label: '小 (60px)' },
                    { value: 'medium', label: '中 (90px)' },
                    { value: 'large', label: '大 (120px)' }
                  ]}
                />
              </Form.Item>

              <Form.Item label="边框样式">
                <Select
                  value={settings.borderStyle}
                  onChange={v => updateSetting('borderStyle', v)}
                  options={[
                    { value: 'solid', label: '实线' },
                    { value: 'dashed', label: '虚线' },
                    { value: 'dotted', label: '点线' },
                    { value: 'double', label: '双线' }
                  ]}
                />
              </Form.Item>

              <Divider style={{ margin: '12px 0' }}>标题文字</Divider>

              <Form.Item label="主标题">
                <Input value={settings.headerText} onChange={e => updateSetting('headerText', e.target.value)} />
              </Form.Item>

              <Form.Item label="副标题">
                <Input value={settings.subheaderText} onChange={e => updateSetting('subheaderText', e.target.value)} />
              </Form.Item>

              <Form.Item label="显示 Logo">
                <Space>
                  <Switch checked={settings.showLogo} onChange={v => updateSetting('showLogo', v)} />
                  {settings.showLogo && (
                    <Upload beforeUpload={handleLogoUpload} showUploadList={false} accept="image/*">
                      <Button size="small" icon={<UploadOutlined />}>上传</Button>
                    </Upload>
                  )}
                  {settings.logo && <Button size="small" danger onClick={() => setSettings(s => ({ ...s, logo: '' }))}>清除</Button>}
                </Space>
              </Form.Item>

              <Divider style={{ margin: '12px 0' }}>显示字段</Divider>

              <Row gutter={8}>
                <Col span={12}><Form.Item label="姓名"><Switch checked={settings.showName} onChange={v => updateSetting('showName', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="电话"><Switch checked={settings.showPhone} onChange={v => updateSetting('showPhone', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="房号"><Switch checked={settings.showRoom} onChange={v => updateSetting('showRoom', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="日期"><Switch checked={settings.showDate} onChange={v => updateSetting('showDate', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="时间"><Switch checked={settings.showTime} onChange={v => updateSetting('showTime', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="目的"><Switch checked={settings.showPurpose} onChange={v => updateSetting('showPurpose', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="预约编号"><Switch checked={settings.showId} onChange={v => updateSetting('showId', v)} /></Form.Item></Col>
                <Col span={12}><Form.Item label="打印时间"><Switch checked={settings.showPrintTime} onChange={v => updateSetting('showPrintTime', v)} /></Form.Item></Col>
              </Row>

              <Divider style={{ margin: '12px 0' }}>
                字号大小 <span style={{ color: '#999', fontSize: 12, fontWeight: 'normal' }}>(留空=按纸张自动)</span>
                <Button
                  size="small"
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={() => setSettings(s => ({
                    ...s,
                    titleFontSize: null,
                    subtitleFontSize: null,
                    infoFontSize: null,
                    labelFontSize: null,
                    idFontSize: null,
                    footerFontSize: null
                  }))}
                >恢复自动</Button>
              </Divider>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="主标题 (pt)">
                    <InputNumber
                      value={settings.titleFontSize}
                      onChange={v => updateSetting('titleFontSize', v)}
                      min={6}
                      max={48}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="自动"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="副标题 (pt)">
                    <InputNumber
                      value={settings.subtitleFontSize}
                      onChange={v => updateSetting('subtitleFontSize', v)}
                      min={5}
                      max={36}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="自动"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="信息值 (pt)">
                    <InputNumber
                      value={settings.infoFontSize}
                      onChange={v => updateSetting('infoFontSize', v)}
                      min={5}
                      max={36}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="自动"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="字段标签 (pt)">
                    <InputNumber
                      value={settings.labelFontSize}
                      onChange={v => updateSetting('labelFontSize', v)}
                      min={5}
                      max={36}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="自动"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="编号 (pt)">
                    <InputNumber
                      value={settings.idFontSize}
                      onChange={v => updateSetting('idFontSize', v)}
                      min={5}
                      max={36}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="自动"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="页脚 (pt)">
                    <InputNumber
                      value={settings.footerFontSize}
                      onChange={v => updateSetting('footerFontSize', v)}
                      min={5}
                      max={36}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder="自动"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        <Col span={16}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SearchOutlined style={{ color: '#fff', fontSize: 16 }} />
                </div>
                <span style={{ fontSize: 16, fontWeight: 600 }}>选择要打印的预约</span>
              </div>
            }
            size="small"
            style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)', marginBottom: 16 }}
            extra={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Select
                  className="toolbar-select"
                  value={communityFilter || undefined}
                  onChange={v => { setCommunityFilter(v); setSelectedRowKeys([]); setPagination(p => ({ ...p, current: 1 })); fetchAppointments(statusFilter, searchText, v, 1, pagination.pageSize); }}
                  style={{ width: 150, borderRadius: 8 }}
                  placeholder={isSuperAdmin ? '全部小区' : '本小区'}
                  disabled={!isSuperAdmin}
                  allowClear={isSuperAdmin}
                  popupMatchSelectWidth={false}
                >
                  {communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: 220,
                  height: 36,
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: '0 11px',
                  background: '#fff',
                  transition: 'border-color 0.3s'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#1890ff'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                >
                  <SearchOutlined style={{ color: '#1890ff', fontSize: 14, marginRight: 8, lineHeight: 1 }} />
                  <input
                    placeholder="搜索姓名/电话/编号"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onPressEnter={() => { setPagination(p => ({ ...p, current: 1 })); fetchAppointments(statusFilter, searchText, communityFilter, 1, pagination.pageSize); }}
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: 14,
                      lineHeight: '34px',
                      height: 34,
                      color: '#333'
                    }}
                  />
                  {searchText && (
                    <CloseCircleFilled
                      style={{ color: '#ccc', fontSize: 14, cursor: 'pointer', lineHeight: 1 }}
                      onClick={() => setSearchText('')}
                    />
                  )}
                </div>
                <Select
                  className="toolbar-select"
                  value={statusFilter}
                  onChange={v => { setStatusFilter(v); setPagination(p => ({ ...p, current: 1 })); fetchAppointments(v, searchText, communityFilter, 1, pagination.pageSize); }}
                  style={{ width: 130, borderRadius: 8 }}
                  popupMatchSelectWidth={false}
                  options={[
                    { value: 'all', label: '全部状态' },
                    { value: 'approved', label: '已通过' },
                    { value: 'checked_in', label: '已核销' },
                    { value: 'pending', label: '待审核' }
                  ]}
                />
                <Button
                  type="primary"
                  icon={<PrinterOutlined />}
                  onClick={handlePrint}
                  disabled={selectedRowKeys.length === 0}
                  style={{
                    height: 36,
                    padding: '0 20px',
                    lineHeight: '34px',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    background: selectedRowKeys.length > 0
                      ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                      : undefined,
                    borderColor: '#389e0d',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6
                  }}
                >
                  打印 ({selectedRowKeys.length})
                </Button>
              </div>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              dataSource={appointments}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                hideDefaultSelections: true,
                preserveSelectedRowKeys: true
              }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showTotal: (t) => `共 ${t} 条记录`,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              onChange={handleTableChange}
              columns={[
                { title: '编号', dataIndex: 'id', width: 60, className: 'table-column-center' },
                { title: '访客', dataIndex: 'visitor_name', width: 90 },
                { title: '电话', dataIndex: 'visitor_phone', width: 110 },
                { title: '房号', dataIndex: 'room_info', ellipsis: true },
                { title: '日期', dataIndex: 'visit_date', width: 100 },
                { title: '状态', dataIndex: 'status', width: 80, render: t => {
                  const map = {
                    approved: <Tag color="green" style={{ borderRadius: 4, padding: '2px 8px' }}>已通过</Tag>,
                    checked_in: <Tag color="blue" style={{ borderRadius: 4, padding: '2px 8px' }}>已核销</Tag>,
                    pending: <Tag color="orange" style={{ borderRadius: 4, padding: '2px 8px' }}>待审核</Tag>,
                    rejected: <Tag color="red" style={{ borderRadius: 4, padding: '2px 8px' }}>已拒绝</Tag>
                  };
                  return map[t] || t;
                }}
              ]}
              style={{ borderRadius: 8 }}
              rowClassName={(record, index) => index % 2 === 0 ? 'table-row-even' : 'table-row-odd'}
            />
          </Card>

          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileTextOutlined style={{ color: '#fff', fontSize: 16 }} />
                </div>
                <span style={{ fontSize: 16, fontWeight: 600 }}>实时预览</span>
              </div>
            }
            size="small"
            style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)' }}
          >
            <div className="preview-area">
              {previewAppointments.length === 0 ? (
                <div className="print-empty">
                  <Empty description="请先选择预约记录" />
                </div>
              ) : (
                <div
                  className="print-preview-paper"
                  style={{
                    width: `${getCurrentPaperDims().width}mm`,
                    minHeight: `${getCurrentPaperDims().height}mm`,
                    padding: isCompactPaper() ? '2mm' : '8mm',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                    borderRadius: isCompactPaper() ? '4px' : '8px'
                  }}
                >
                  <div className="print-grid">
                    {previewAppointments.map((a, i) => renderCard(a, i))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <div className="print-area" ref={printRef} style={{ display: previewMode ? 'block' : 'none' }}>
        <div className="print-grid">
          {previewAppointments.map((a, i) => renderCard(a, i))}
        </div>
      </div>
    </div>
  );
};

export default PrintTemplate;
